'use client'
import { useEffect } from 'react'

/** DOM id for an item's scroll target, derived from its `value`. */
export const itemDomId = (name: string) => `accordion-item-${name}`

/**
 * In-memory "open this item" channel. Lets a component anywhere on the page open
 * an accordion item instantly, with no URL navigation or refetch. Use this
 * (not the URL) for same-page triggers
 */
type Listener = (value: string) => void

const listeners = new Map<string, Set<Listener>>()

const DEFAULT_CHANNEL = 'default'

export const openAccordionItem = (name: string, channel: string = DEFAULT_CHANNEL) => {
  listeners.get(channel)?.forEach(listener => listener(name))
}

const subscribe = (channel: string, listener: Listener) => {
  let set = listeners.get(channel)

  if (!set) {
    set = new Set()
    listeners.set(channel, set)
  }

  set.add(listener)

  return () => {
    set?.delete(listener)

    if (set && set.size === 0) {
      listeners.delete(channel)
    }
  }
}

export const useAccordionOpenSignal = (
  onOpen: (name: string) => void,
  channel: string = DEFAULT_CHANNEL
) => {
  useEffect(() => subscribe(channel, onOpen), [channel, onOpen])
}
